import { useEffect, useRef, type MutableRefObject } from "react";
import { useBlocker, type Blocker } from "react-router-dom";

/**
 * Warns before unsaved work is lost: in-app navigation shows `blocker` for the page to render as a dialog,
 * and closing/reloading the tab triggers the browser's own prompt. Set `allowLeave.current = true`
 * right before a navigation the page makes itself (after saving, after deleting).
 */
export function useUnsavedGuard(dirty: boolean): { blocker: Blocker; allowLeave: MutableRefObject<boolean> } {
  const allowLeave = useRef(false);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !allowLeave.current && dirtyRef.current && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  return { blocker, allowLeave };
}
